import { ChevronRight } from "lucide-react"
import React from "react"
import { NavLink, useLocation } from "react-router-dom"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/shadcn/collapsible.tsx"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/shadcn/sidebar.tsx"

interface NavItem {
  title: string
  icon?: React.ElementType
  items?: SubNavItem[]
}

interface SubNavItem {
  title: string
  url: string
}

interface NavMainProps {
  items: NavItem[]
  title?: string
}

const FOCUS_RING =
  "outline-none focus-visible:ring-2 focus-visible:ring-[var(--brown)] focus-visible:ring-offset-1"

function NavSubItem({ subItem }: { subItem: SubNavItem }) {
  const { pathname } = useLocation()
  const isActive = pathname === subItem.url

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        className={`transition-colors ${FOCUS_RING} ${isActive ? "bg-brown-50 text-brown-500" : ""}`}
        asChild
      >
        <NavLink to={subItem.url}>
          <span>{subItem.title}</span>
        </NavLink>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
}

function NavCollapsibleItem({ item }: { item: NavItem }) {
  const { pathname } = useLocation()
  const isAnySubItemActive =
    item.items?.some((subItem) => pathname === subItem.url) ?? false

  return (
    <Collapsible
      asChild
      className="group/collapsible"
      open={isAnySubItemActive ? true : undefined}
      defaultOpen={isAnySubItemActive}
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            className={`h-12 transition-colors ${FOCUS_RING} [@media(hover:hover)]:hover:text-brown-500 ${
              isAnySubItemActive ? "text-brown-500" : ""
            }`}
            tooltip={item.title}
          >
            {item.icon && React.createElement(item.icon)}
            <span>{item.title}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items?.map((subItem) => (
              <NavSubItem key={subItem.title} subItem={subItem} />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

export function NavMain({ items, title }: NavMainProps) {
  return (
    <SidebarGroup>
      {title && <SidebarGroupLabel>{title}</SidebarGroupLabel>}
      <SidebarMenu>
        {items.map((item) => (
          <NavCollapsibleItem key={item.title} item={item} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}
